"use client";

import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTheme } from "../../Components/ThemeContext";

const containerVariants = {
  hidden: { opacity: 0, y: 30 },
  show:   { opacity: 1, y: 0, transition: { staggerChildren: 0.15 } },
};

const itemVariants = {
  hidden: { opacity: 0, y: 15 },
  show:   { opacity: 1, y: 0 },
};

const colorClasses = {
  green:  "bg-green-400",
  green2: "bg-green-400",
  purple: "bg-purple-400",
  pink:   "bg-pink-400",
  yellow: "bg-yellow-400",
  teal:   "bg-teal-400",
};

export default function TechStackAccordionForAIAutomation() {
  const [isExpanded, setIsExpanded] = useState(false);
  const { isDark } = useTheme();

  // ✅ SEO: updated titles to AI Agent & Automation
  const techStack = {
    mlFrameworks:    { title: "Machine Learning Frameworks",  color: "green",  items: ["TensorFlow", "PyTorch", "scikit-learn", "Keras"] },
    aiTools:         { title: "AI Agent Development Tools",   color: "purple", items: ["OpenAI API", "LangChain", "Hugging Face", "Google Vertex AI"] },
    automationTools: { title: "Automation & Workflow Tools",  color: "green2", items: ["UiPath", "Zapier", "Power Automate", "Automation Anywhere"] },
    nlp:             { title: "Natural Language Processing",  color: "pink",   items: ["GPT Models", "BERT", "spaCy", "NLTK"] },
    dataProcessing:  { title: "Data Processing & Analytics",  color: "yellow", items: ["Pandas", "NumPy", "Apache Spark", "Tableau"] },
    cloudPlatforms:  { title: "Cloud AI Platforms",           color: "teal",   items: ["AWS AI/ML", "Google Cloud AI", "Azure Cognitive Services"] },
  };

  const wrapperBg = isDark ? "bg-zinc-900"   : "bg-[#1a2e1a]";
  const cardBg    = isDark ? "bg-zinc-800"   : "bg-[#0f1f0f]";
  const subText   = isDark ? "text-gray-400" : "text-gray-300";

  return (
    // ✅ SEO: <section> landmark with aria-labelledby
    <section
      aria-labelledby="ai-tech-stack-heading"
      className="w-full max-w-6xl mx-auto p-4 -mt-8 md:-mt-12"
    >
      {/* ✅ SEO: sr-only H2 — keywords always in DOM */}
      <h2 id="ai-tech-stack-heading" className="sr-only">
        AI Agent & Automation Tech Stack — TensorFlow, PyTorch, OpenAI API, LangChain, UiPath, Zapier, GPT
      </h2>

      {/* Accordion trigger */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        aria-expanded={isExpanded}
        aria-controls="ai-tech-stack-content"
        className={`w-full ${wrapperBg} border-2 border-green-400 rounded-2xl p-6 flex items-center justify-between group transition-all duration-300 hover:shadow-[0_0_20px_rgba(34,197,94,0.6)]`}
      >
        <div className="flex items-center gap-4">
          <div
            className="w-12 h-12 bg-gradient-to-br from-green-400 to-indigo-600 rounded-xl flex items-center justify-center flex-shrink-0"
            aria-hidden="true"
          >
            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
          </div>
          <div className="text-left">
            <p className="text-white text-xl md:text-2xl font-semibold">For AI & Automation Innovators</p>
            <p className={`${subText} text-sm md:text-base mt-1`}>
              Expand to explore the technologies powering our AI agent & automation solutions
            </p>
          </div>
        </div>
        <div className="flex-shrink-0" aria-hidden="true">
          <svg
            className={`w-6 h-6 text-gray-400 transition-transform duration-300 ${isExpanded ? "rotate-180" : ""}`}
            fill="none" stroke="currentColor" viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </button>

      {/* ✅ SEO: id matches aria-controls */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            id="ai-tech-stack-content"
            role="region"
            aria-labelledby="ai-tech-stack-heading"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.5, ease: "easeInOut" }}
            className="mt-4 overflow-hidden"
          >
            <motion.div
              className={`${wrapperBg} rounded-2xl p-6 md:p-8`}
              variants={containerVariants}
              initial="hidden"
              animate="show"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
                {Object.entries(techStack).map(([key, category]) => (
                  <motion.div
                    key={key}
                    variants={itemVariants}
                    className={`space-y-4 ${cardBg} p-4 rounded-xl hover:shadow-lg hover:shadow-green-400 transition-all`}
                  >
                    <div className="flex items-center gap-2">
                      <div
                        className={`w-2 h-2 rounded-full flex-shrink-0 ${colorClasses[category.color]}`}
                        aria-hidden="true"
                      />
                      {/* ✅ SEO: h3 under sr-only h2 */}
                      <h3 className="text-white font-semibold text-lg">{category.title}</h3>
                    </div>
                    {/* ✅ SEO: <ul><li> — each tool individually crawlable */}
                    <ul className="flex flex-wrap gap-x-2 gap-y-1" aria-label={`${category.title} tools`}>
                      {category.items.map((item, i) => (
                        <li key={item} className="text-gray-300 text-sm">
                          {item}{i < category.items.length - 1 ? "," : ""}
                        </li>
                      ))}
                    </ul>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ✅ SEO: always-in-DOM hidden paragraph — all keywords indexed even when accordion is closed */}
      <p className="sr-only">
        Our AI agent and automation tech stack includes: TensorFlow, PyTorch, scikit-learn, Keras for machine learning;
        OpenAI API, LangChain, Hugging Face, Google Vertex AI for AI agent development;
        UiPath, Zapier, Power Automate, Automation Anywhere for workflow automation;
        GPT Models, BERT, spaCy, NLTK for natural language processing;
        Pandas, NumPy, Apache Spark, Tableau for data processing and analytics;
        AWS AI/ML, Google Cloud AI, Azure Cognitive Services for cloud AI platforms.
      </p>
    </section>
  );
}